/**
 * Traxión + el registro de templates.
 *
 * Extraído de `theme-silia.ts` (paso 03, ver `theme/spec.md` §9). El paso 06
 * lo convierte en un `BrandDefinition` tipado y borra `toTraxion()`.
 */

import type { ThemeConfig } from 'antd';
import { radius } from '../primitives';
import { emitAssetVars, traxionAssets } from './assets';
import type { BrandRamp } from '../primitives';
import { cssVariables } from '../emitVars';
import { antdToken } from '../tokens';
import { buildComponents } from '../components';
import { antdTheme, cssVarConfig } from '../config';

/* ═══════════════════════════════════════════════════════════════════════
   8. TEMPLATES — runtime multi-brand
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * A template is a cohesive set of tokens that re-skins the whole app without
 * touching component logic. Two mechanisms work together:
 *
 *   1. A class on `<body>` (`bodyClass`) overrides the `--silia-*` variables,
 *      which is what hand-written CSS reads.
 *   2. `themeFor(id)` returns the AntD `ConfigProvider` config, so AntD
 *      primitives repaint too.
 *
 * ── How this relates to REACT_APP_THEME ──────────────────────────────────
 * They are different axes and must not be confused:
 *
 *   REACT_APP_THEME  picks the WHOLE theme module at BUILD time — silia, eca,
 *                    lipu, neutral. Different products, different token files.
 *   TemplateId       re-skins the Silia product at RUNTIME, for a customer who
 *                    wants their own brand inside the same app.
 *
 * So a template is a sub-axis of `silia`, not a competitor to it. Nothing here
 * changes how `theme.ts` resolves a module.
 */
export type TemplateId = 'default' | 'traxion';

export type TemplateDef = {
  id: TemplateId;
  label: string;
  description: string;
  /** Body class that activates the matching `--silia-*` overrides. */
  bodyClass: string;
  /** Passed to AntD's ConfigProvider so its primitives repaint too. */
  themeConfig: ThemeConfig;
  /** CSS emitted under `body.<bodyClass>`; empty for the default template. */
  cssOverrides: string;
};

/**
 * Traxión — corporate logistics palette, per their DS.
 *
 * Their system reserves all chromatic accent for the lime primary, so every
 * place Silia hints purple becomes a neutral gray. Radius base is 6, not 12,
 * and the family is Inter.
 */
const TRAXION = {
  primary: '#d0df00',
  primaryDark: '#b5bd00',
  secondary: '#63666a',
  secondaryLight: '#d0d0ce',
  secondaryMedium: '#97999b',
  surfaceMuted: '#f8f9fa',
  bgLayout: '#f8f9fa',
  bgContainer: '#ffffff',
  text: '#0f172a',
  textSecondary: '#63666a',
  textMuted: '#97999b',
  border: '#d0d0ce',
  borderSubtle: '#e5e7eb',
  black: '#000000',
} as const;

/**
 * Traxión's `--silia-*` overrides.
 *
 * Two things here are load-bearing and easy to get wrong:
 *
 *   · The kind/node families are REDECLARED, not inherited. A `var()` alias
 *     resolves in the element where it is declared, so `--silia-kind-folder-bg:
 *     var(--silia-accent-bg)` written in `:root` stays frozen on Silia's purple
 *     even after this block remaps `--silia-accent-bg`.
 *   · The CTA shadows are redeclared for the same reason: their colour is
 *     written inside the value, not referenced.
 */
const TRAXION_CSS = `
  body.template-traxion {
    /* Brand */
    --silia-ink-strong: ${TRAXION.black};
    --silia-primary: ${TRAXION.primary};
    --silia-primary-bg-subtle: rgba(208, 223, 0, 0.18);
    --silia-primary-bg: rgba(208, 223, 0, 0.3);
    --silia-primary-border: ${TRAXION.primaryDark};
    --silia-shadow-cta: 0 2px 6px rgba(208, 223, 0, 0.45);
    --silia-shadow-cta-hover: 0 4px 10px rgba(208, 223, 0, 0.65);

    /* The purple slots become Traxión's gray neutrals. */
    --silia-accent-bg-subtle: #fbfbfc;
    --silia-accent-bg: ${TRAXION.surfaceMuted};
    --silia-accent-border: ${TRAXION.borderSubtle};
    --silia-accent-border-strong: ${TRAXION.secondaryLight};
    --silia-accent-fg-muted: #7c7f83;
    --silia-accent-fg: ${TRAXION.secondary};
    --silia-accent-fg-strong: #3f4145;
    --silia-accent-solid: #2b2d30;

    /* Surfaces */
    --silia-bg-layout: ${TRAXION.bgLayout};
    --silia-bg-container: ${TRAXION.bgContainer};
    --silia-bg-elevated: ${TRAXION.bgContainer};
    --silia-surface-tint-1: #fafafa;
    --silia-surface-tint-2: #f3f4f6;
    --silia-surface-tint-3: ${TRAXION.borderSubtle};
    --silia-input-addon: #f3f4f6;

    /* Text */
    --silia-text: ${TRAXION.text};
    --silia-text-description: ${TRAXION.textSecondary};
    --silia-text-icon: ${TRAXION.textMuted};
    --silia-text-placeholder: #b0b2b5;
    --silia-text-inverse: #ffffff;

    /* Borders */
    --silia-border: ${TRAXION.border};
    --silia-border-subtle: ${TRAXION.borderSubtle};
    --silia-fill-content: ${TRAXION.surfaceMuted};
    --silia-split: ${TRAXION.borderSubtle};

    /* Semantic */
    --silia-success-bg: #dcfce7;
    --silia-success-fg: #15803d;
    --silia-success-fg-strong: #14532d;
    --silia-success-bd: #86efac;
    --silia-warning-bg: #fef3c7;
    --silia-warning-fg: #a16207;
    --silia-warning-fg-strong: #713f12;
    --silia-warning-bd: #fcd34d;
    --silia-danger-bg: #fee2e2;
    --silia-danger-fg: #b91c1c;
    --silia-danger-fg-strong: #7f1d1d;
    --silia-danger-bd: #fca5a5;
    --silia-info-bg: #cffafe;
    --silia-info-fg: #0e7490;
    --silia-info-fg-strong: #164e63;
    --silia-info-bd: #67e8f9;

    /* Object kind. Traxión has no seven-pastel system — its DS reserves colour
       for the lime and resolves everything else with neutrals and its four
       semantic ramps. So the families borrow: an RTA is not "a success", that
       is just the green available. */
    --silia-kind-folder-bg: ${TRAXION.surfaceMuted};
    --silia-kind-folder-fg: ${TRAXION.secondary};
    --silia-kind-folder-bd: ${TRAXION.borderSubtle};
    --silia-kind-agent-bg: ${TRAXION.primary};
    --silia-kind-agent-fg: ${TRAXION.black};
    --silia-kind-agent-bd: #b7c400;
    --silia-kind-agent-soft-bg: #f8fbdb;
    --silia-kind-agent-soft-fg: #4f5600;
    --silia-kind-agent-soft-bd: #e6ee8c;
    --silia-kind-qa-bg: #fee2e2;
    --silia-kind-qa-fg: #b91c1c;
    --silia-kind-qa-bd: #fca5a5;
    --silia-kind-rta-bg: #dcfce7;
    --silia-kind-rta-fg: #15803d;
    --silia-kind-rta-bd: #86efac;
    --silia-kind-data-view-bg: #fef3c7;
    --silia-kind-data-view-fg: #854d06;
    --silia-kind-data-view-bd: #fcd34d;
    --silia-kind-dashboard-bg: #cffafe;
    --silia-kind-dashboard-fg: #0e7490;
    --silia-kind-dashboard-bd: #67e8f9;
    --silia-kind-file-bg: #f3f4f6;
    --silia-kind-file-fg: ${TRAXION.secondary};
    --silia-kind-file-bd: ${TRAXION.borderSubtle};

    /* Canvas node type — same borrowed ramps. */
    --silia-node-tool-bg: #cffafe;
    --silia-node-tool-fg: #164e63;
    --silia-node-tool-bd: #67e8f9;
    --silia-node-mcp-bg: #f3f4f6;
    --silia-node-mcp-fg: #3f4145;
    --silia-node-mcp-bd: ${TRAXION.secondaryLight};
    --silia-node-table-bg: #dcfce7;
    --silia-node-table-fg: #14532d;
    --silia-node-table-bd: #86efac;
    --silia-node-agent-bg: #fef3c7;
    --silia-node-agent-fg: #713f12;
    --silia-node-agent-bd: #fcd34d;

    /* Elevation — Traxión does not tint its neutrals violet, so the shadow is
       pure black and the focus ring follows their gray. */
    --silia-shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.02), 0 1px 6px rgba(0, 0, 0, 0.02), 0 1px 2px rgba(0, 0, 0, 0.03);
    --silia-shadow-md: 0 6px 16px rgba(0, 0, 0, 0.06), 0 3px 6px rgba(0, 0, 0, 0.04);
    --silia-shadow-lg: 0 12px 32px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06);
    --silia-shadow-xl: 0 18px 50px rgba(0, 0, 0, 0.32);
    --silia-focus-ring: 0 0 0 3px ${TRAXION.secondaryLight};

    /* Typography & shape — Inter, 6px base radius. */
    --silia-font-sans: 'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif;
    --silia-radius-xs: ${radius.xs}px;
    --silia-radius-sm: ${radius.compact}px;
    --silia-radius-md: ${radius.compact}px;
    --silia-radius-lg: ${radius.sm}px;

    /* Assets. Declarados, no heredados: sin estas líneas Traxión se queda con
       los de :root, que son los de Silia, y nadie se entera porque una
       herencia no aparece en el diff. Hoy apuntan igual a los de Silia —ver
       el TODO en brands/assets.ts— pero la deuda queda escrita y testeada. */
${emitAssetVars(traxionAssets)}

    font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif;
    background: ${TRAXION.bgLayout};
    color: ${TRAXION.text};
  }

  body.template-traxion h1,
  body.template-traxion h2,
  body.template-traxion h3,
  body.template-traxion h4,
  body.template-traxion h5,
  body.template-traxion h6,
  body.template-traxion input,
  body.template-traxion textarea,
  body.template-traxion button,
  body.template-traxion select {
    font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif;
  }
`;

/**
 * Silia's brand hex -> Traxión's equivalent.
 *
 * `TRAXION_ANTD` builds its component set by spreading the default one, which
 * carries Silia's literal colours. Re-pointing each affected component by hand
 * does not hold: thirty-odd tokens across sixteen components were still
 * painting Silia purple under Traxión, and every component added later would
 * start wrong the same way — silently, since the value is perfectly valid,
 * just the other brand's.
 *
 * So the spread is remapped rather than copied. Traxión reserves all chromatic
 * accent for its lime, so the purple ramp lands on its gray neutrals, in order:
 * light stays light, dark stays dark, and a border keeps reading as a border.
 */
/**
 * La rampa de Traxión, declarada — no traducida desde la de Silia.
 *
 * Antes esto era `TRAXION_FROM_SILIA` + `toTraxion()`: un mapa hex→hex y una
 * recursión que cambiaba cualquier string coincidente en los 60 componentes.
 * Traxión reserva todo el acento cromático para su lima, así que la rampa
 * violeta aterriza en sus grises: claro sigue claro, oscuro sigue oscuro, y un
 * borde sigue leyéndose como borde.
 */
const TRAXION_RAMP: BrandRamp = {
  lime: TRAXION.primary,
  black: TRAXION.black,
  n100: '#fbfbfc',
  n200: TRAXION.surfaceMuted,
  n400: TRAXION.borderSubtle,
  n500: TRAXION.secondaryLight,
  n600: '#7c7f83',
  n700: TRAXION.secondary,
  n800: '#3f4145',
  n900: '#2b2d30',
};

/** Los 60 componentes construidos con la rampa de Traxión. */
const TRAXION_BASE = buildComponents(TRAXION_RAMP);

const TRAXION_ANTD: ThemeConfig = {
  token: {
    ...antdToken,
    colorPrimary: TRAXION.primary,
    colorBgLayout: TRAXION.bgLayout,
    colorBgContainer: TRAXION.bgContainer,
    colorBgElevated: TRAXION.surfaceMuted,
    colorText: TRAXION.text,
    colorTextDescription: TRAXION.textSecondary,
    colorTextSecondary: TRAXION.textSecondary,
    colorTextTertiary: TRAXION.textMuted,
    colorTextLightSolid: TRAXION.black,
    colorBorder: TRAXION.border,
    colorBorderSecondary: TRAXION.borderSubtle,
    colorFillContent: TRAXION.surfaceMuted,
    colorSplit: TRAXION.borderSubtle,
    colorSuccess: '#15803d',
    colorWarning: '#a16207',
    colorError: '#b91c1c',
    colorInfo: '#0e7490',
    // Pinned for the same reason as the base theme: without these AntD derives
    // the panel from `colorInfo` and the ramp override alone is not enough.
    colorInfoBg: '#cffafe',
    colorInfoBorder: '#67e8f9',
    borderRadius: radius.compact,
    borderRadiusLG: radius.sm,
    borderRadiusSM: radius.xs,
    fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
    lineHeight: 20 / 14,
  },
  components: {
    ...TRAXION_BASE,
    Button: {
      ...TRAXION_BASE.Button,
      colorTextLightSolid: TRAXION.black,
      controlHeightLG: 48,
      borderRadius: radius.compact,
      borderRadiusLG: radius.compact,
      borderRadiusSM: radius.compact,
      /** Default-button hover is gray-on-gray — Traxión reserves lime for
          primary CTAs only (DS rule). */
      defaultHoverBg: TRAXION.surfaceMuted,
      defaultHoverColor: TRAXION.text,
      defaultHoverBorderColor: TRAXION.secondary,
      defaultActiveBg: TRAXION.surfaceMuted,
      defaultActiveColor: TRAXION.text,
      defaultActiveBorderColor: TRAXION.secondary,
    },
    Input: {
      ...TRAXION_BASE.Input,
      borderRadius: radius.compact,
      activeBorderColor: TRAXION.secondaryMedium,
      hoverBorderColor: TRAXION.secondaryLight,
      activeShadow: `0 0 0 3px ${TRAXION.secondaryLight}`,
    },
    Select: { ...TRAXION_BASE.Select, borderRadius: radius.compact },
    DatePicker: { ...TRAXION_BASE.DatePicker, borderRadius: radius.compact },
    Card: { ...TRAXION_BASE.Card, borderRadiusLG: radius.sm },
    Segmented: { ...TRAXION_BASE.Segmented, borderRadius: radius.compact },
    Layout: {
      ...TRAXION_BASE.Layout,
      colorBgLayout: TRAXION.bgLayout,
      bodyBg: TRAXION.bgLayout,
      siderBg: TRAXION.bgContainer,
    },
    Divider: { colorSplit: TRAXION.borderSubtle },
    Breadcrumb: {
      itemColor: TRAXION.textSecondary,
      lastItemColor: TRAXION.text,
      separatorColor: TRAXION.textMuted,
    },
    /** Tooltip paints its text with `colorTextLightSolid`, which under Traxión
        is black (so the lime primary button gets dark text). On the tooltip's
        dark background that black does not contrast — forced white here. */
    Tooltip: { ...TRAXION_BASE.Tooltip, colorTextLightSolid: '#ffffff' },
  },
  /* Same variable mode as the default template. Omitting it here would silently
     drop the app out of cssVar the moment a template is selected. */
  cssVar: cssVarConfig,
  hashed: false,
};

export const TEMPLATES: Record<TemplateId, TemplateDef> = {
  default: {
    id: 'default',
    label: 'Silia',
    description: 'Marca por defecto — lime + purple, DM Sans, radius 12px',
    bodyClass: '',
    themeConfig: antdTheme,
    cssOverrides: '',
  },
  traxion: {
    id: 'traxion',
    label: 'Traxión',
    description: 'Plataforma logística — lime + grays neutrales, Inter, radius 6px',
    bodyClass: 'template-traxion',
    themeConfig: TRAXION_ANTD,
    cssOverrides: TRAXION_CSS,
  },
};

export const TEMPLATE_LIST: TemplateDef[] = [TEMPLATES.default, TEMPLATES.traxion];

export const DEFAULT_TEMPLATE: TemplateId = 'default';

/** AntD config for a template, falling back to the default. */
export function themeFor(id: TemplateId): ThemeConfig {
  return TEMPLATES[id]?.themeConfig ?? TEMPLATES.default.themeConfig;
}

/** Body class for a template — apply it to `document.body`. */
export function bodyClassFor(id: TemplateId): string {
  return TEMPLATES[id]?.bodyClass ?? '';
}

/**
 * Every template's CSS overrides, concatenated. Inject alongside
 * `cssVariables`; the rules only apply once the matching body class is set, so
 * shipping them all costs nothing until a template is activated.
 */
export const templateCssOverrides: string = TEMPLATE_LIST.map((t) => t.cssOverrides)
  .filter(Boolean)
  .join('\n');

/** `cssVariables` plus every template override — the whole stylesheet. */
export const cssVariablesWithTemplates: string = `${cssVariables}\n${templateCssOverrides}`;
